import { useState } from "react";
import { createPortal } from "react-dom";
import { API } from "../../../cache";
import "../styles/MultiProjectTaskModal.css";

function buildInitialRows(projectOptions) {
  return projectOptions.map((p) => ({
    id: p.id,
    project_name: p.project_name,
    customer_name: p.customer_name,
    checked: false,
    epic_id: "",
    epicOptions: [],
    epicLoading: false,
    epicLoaded: false,
    tmm_member: "",
    tmm_team_id: "",
    tmm_team_name: "",
  }));
}

export default function MultiProjectTaskModal({
  projectOptions,
  users,
  resolveAssigneeTeam,
  isPrivateVisibility,
  loggedInUserName,
  taskCommon,
  onSubmit,
  onClose,
}) {
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState(() => buildInitialRows(projectOptions));
  const [rowErrors, setRowErrors] = useState({});
  const [topError, setTopError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadEpicsFor = async (projectId) => {
    setRows((prev) => prev.map((r) => (r.id === projectId ? { ...r, epicLoading: true } : r)));
    let list = [];
    try {
      const res = await fetch(
        `${API}/epics?project_id=${encodeURIComponent(projectId)}&include_closed=false`,
        { credentials: "include" }
      );
      const data = res.ok ? await res.json() : [];
      list = (Array.isArray(data) ? data : []).map((e) => ({
        id: e.id,
        epic_code: e.epic_code || "",
        epic_name: e.epic_name || "",
      }));
    } catch {
      list = [];
    }
    setRows((prev) => prev.map((r) => (r.id === projectId
      ? { ...r, epicOptions: list, epicLoading: false, epicLoaded: true, epic_id: list.length === 1 ? list[0].id : r.epic_id }
      : r)));
  };

  const toggleRow = (projectId) => {
    const row = rows.find((r) => r.id === projectId);
    if (!row) return;
    const nextChecked = !row.checked;
    setRows((prev) => prev.map((r) => (r.id === projectId ? { ...r, checked: nextChecked } : r)));
    if (topError) setTopError("");
    if (rowErrors[projectId]) setRowErrors((prev) => ({ ...prev, [projectId]: "" }));
    if (!nextChecked) return;
    if (!row.epicLoaded) loadEpicsFor(projectId);
    if (isPrivateVisibility && loggedInUserName && !row.tmm_member) {
      setRowAssignee(projectId, loggedInUserName);
    }
  };

  const setRowEpic = (projectId, epicId) => {
    setRows((prev) => prev.map((r) => (r.id === projectId ? { ...r, epic_id: epicId } : r)));
    if (rowErrors[projectId]) setRowErrors((prev) => ({ ...prev, [projectId]: "" }));
  };

  const setRowAssignee = async (projectId, username) => {
    setRows((prev) => prev.map((r) => (r.id === projectId ? { ...r, tmm_member: username } : r)));
    if (rowErrors[projectId]) setRowErrors((prev) => ({ ...prev, [projectId]: "" }));
    const resolved = await resolveAssigneeTeam(username);
    setRows((prev) => prev.map((r) => (r.id === projectId
      ? { ...r, tmm_team_id: resolved?.id || "", tmm_team_name: resolved?.team_name || "" }
      : r)));
  };

  const q = search.trim().toLowerCase();
  const filteredRows = !q
    ? rows
    : rows.filter((r) =>
        r.project_name?.toLowerCase().includes(q) || r.customer_name?.toLowerCase().includes(q)
      );

  const checkedCount = rows.filter((r) => r.checked).length;

  const handleSubmit = async () => {
    const checked = rows.filter((r) => r.checked);
    if (checked.length === 0) {
      setTopError("Select at least one project");
      return;
    }
    const nextErrors = {};
    checked.forEach((r) => {
      if (!r.epic_id) nextErrors[r.id] = "Select an epic";
      else if (!r.tmm_member) nextErrors[r.id] = "Select an assignee";
    });
    if (Object.keys(nextErrors).length > 0) {
      setRowErrors(nextErrors);
      return;
    }
    setSubmitting(true);
    await onSubmit({
      title: taskCommon.title,
      description: taskCommon.description,
      project_epics: checked.map((r) => ({
        project_id:    r.id,
        epic_id:       r.epic_id,
        tmm_member:    r.tmm_member,
        tmm_team_id:   r.tmm_team_id,
        tmm_team_name: r.tmm_team_name,
      })),
      feature_id: taskCommon.feature_id,
      sub_feature_id: taskCommon.sub_feature_id,
      customer_due_date: taskCommon.customer_due_date,
      am_name: taskCommon.am_name,
      new_enhancement: taskCommon.new_enhancement,
      visibility: taskCommon.visibility,
      source: taskCommon.source,
      reporting_channel: taskCommon.reporting_channel,
      effort_hours: taskCommon.effort_hours,
      effort_minutes: taskCommon.effort_minutes,
    });
    setSubmitting(false);
  };

  return createPortal(
    <div className="mv2-mpm-overlay" onClick={submitting ? undefined : onClose}>
      <div className="mv2-mpm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mv2-mpm-header">
          <span className="mv2-mpm-title">Select Projects</span>
          <button className="mv2-mpm-close" onClick={onClose} aria-label="Close" disabled={submitting}>✕</button>
        </div>

        <div className="mv2-mpm-toolbar">
          <input
            className="mv2-mpm-search"
            placeholder="Search projects…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="mv2-mpm-count">
            <span className="mv2-mpm-count-num">{checkedCount}</span> project{checkedCount === 1 ? "" : "s"} selected
          </span>
        </div>

        {topError && <div className="mv2-mpm-toperror">{topError}</div>}

        <div className="mv2-mpm-body">
          {filteredRows.length === 0 ? (
            <div className="mv2-mpm-state">No matching projects.</div>
          ) : (
            <table className="mv2-mpm-table">
              <colgroup>
                <col style={{ width: 36 }} />
                <col style={{ width: "34%" }} />
                <col style={{ width: "33%" }} />
                <col style={{ width: "33%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="mv2-mpm-th" />
                  <th className="mv2-mpm-th">Project</th>
                  <th className="mv2-mpm-th">Epic</th>
                  <th className="mv2-mpm-th">Assignee</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <tr key={r.id} className={r.checked ? "mv2-mpm-row mv2-mpm-row--checked" : "mv2-mpm-row"}>
                    <td className="mv2-mpm-td mv2-mpm-td--check">
                      <input
                        type="checkbox"
                        className="mv2-mpm-check"
                        checked={r.checked}
                        disabled={submitting}
                        onChange={() => toggleRow(r.id)}
                      />
                    </td>
                    <td className="mv2-mpm-td mv2-mpm-td--project">
                      <label className="mv2-mpm-project-label" onClick={() => toggleRow(r.id)}>
                        <span className="mv2-mpm-project-name">{r.project_name}</span>
                        {r.customer_name && <span className="mv2-mpm-project-customer">{r.customer_name}</span>}
                      </label>
                    </td>
                    <td className="mv2-mpm-td">
                      <select
                        className={`mv2-mpm-select${rowErrors[r.id] === "Select an epic" ? " mv2-mpm-select--error" : ""}`}
                        value={r.epic_id}
                        disabled={!r.checked || r.epicLoading || submitting}
                        onChange={(e) => setRowEpic(r.id, e.target.value)}
                      >
                        <option value="">
                          {!r.checked ? "—" : r.epicLoading ? "Loading…" : r.epicOptions.length ? "Select epic…" : "No open epics"}
                        </option>
                        {r.epicOptions.map((e) => (
                          <option key={e.id} value={e.id}>
                            {e.epic_code ? `${e.epic_code} · ${e.epic_name}` : e.epic_name}
                          </option>
                        ))}
                      </select>
                      {rowErrors[r.id] === "Select an epic" && <span className="mv2-mpm-error">Select an epic</span>}
                    </td>
                    <td className="mv2-mpm-td">
                      <select
                        className={`mv2-mpm-select${rowErrors[r.id] === "Select an assignee" ? " mv2-mpm-select--error" : ""}`}
                        value={r.tmm_member}
                        disabled={!r.checked || isPrivateVisibility || submitting}
                        onChange={(e) => setRowAssignee(r.id, e.target.value)}
                      >
                        <option value="">{!r.checked ? "—" : "Select assignee…"}</option>
                        {users.map((u) => (
                          <option key={u.id} value={u.user_name}>{u.display_name || u.user_name}</option>
                        ))}
                      </select>
                      {rowErrors[r.id] === "Select an assignee" && <span className="mv2-mpm-error">Select an assignee</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mv2-mpm-footer">
          <button className="mv2-mpm-btn-cancel" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="mv2-mpm-btn-submit" onClick={handleSubmit} disabled={submitting}>
            {submitting
              ? "Creating…"
              : checkedCount > 0
                ? `Create ${checkedCount} task${checkedCount === 1 ? "" : "s"}`
                : "Create tasks"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
